import { Navigate } from 'react-router';
import { usePlatformMetrics } from '../hooks/useReview';
import { useAuthStore } from '../stores/authStore';

function formatNum(n: number | null, digits = 1): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

function formatPct(n: number | null): string {
  if (n == null) return '—';
  return `${Math.round(n * 100)}%`;
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 px-4 py-3 shadow-xs">
      <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-surface-500">
        {label}
      </div>
      <div className="mt-2 font-serif text-3xl font-medium tabular-nums leading-none text-surface-900">
        {value}
      </div>
      {hint && <div className="mt-1.5 text-[11px] text-surface-500">{hint}</div>}
    </div>
  );
}

// Platform metrics dashboard (backlog B2). The data + math live server-side
// (aggregatePlatformMetrics); this just renders the figures. Platform-admin only.
export function MetricsPage() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.userType === 'PLATFORM_ADMIN';
  const { data, isLoading, isError } = usePlatformMetrics(isPlatformAdmin);

  // Reviewers don't get the platform view (the endpoint 403s for them anyway).
  if (user && !isPlatformAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex-1 overflow-auto px-6 py-5">
      <h1 className="text-lg font-bold text-surface-900">Platform Metrics</h1>
      <p className="mt-0.5 text-xs text-surface-500">
        GA decision-gate metrics across all monthly closes.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-900 border-t-transparent" />
        </div>
      ) : isError || !data ? (
        <p className="mt-6 text-sm text-neg">Couldn&apos;t load metrics.</p>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          <MetricCard
            label="Closes"
            value={formatNum(data.closeCount, 0)}
            hint={`${formatNum(data.approvedCount, 0)} signed off`}
          />
          <MetricCard
            label="Avg days to close"
            value={formatNum(data.avgDaysToClose, 1)}
            hint="creation → sign-off"
          />
          <MetricCard
            label="Avg automation"
            value={formatPct(data.avgAiAutomationRate)}
            hint="AI-categorized share"
          />
          <MetricCard
            label="Avg review mins"
            value={formatNum(data.avgReviewMinutes, 1)}
            hint={`${formatNum(data.totalReviewMinutes, 0)} total`}
          />
          <MetricCard
            label="Transactions"
            value={formatNum(data.totalTransactions, 0)}
            hint="across all closes"
          />
          <MetricCard
            label="Flags"
            value={formatNum(data.totalFlags, 0)}
            hint={`${formatNum(data.avgFlagsPerClose, 1)} avg/close`}
          />
        </div>
      )}
    </div>
  );
}
